/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { colors, CustomSwitch } from '../../components';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import { updateNotificationPreferences as updateNotificationPreferencesService } from '../../services/user-service';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { updateNotificationPreferences } from '../../store/reducers/userSlice';
import { trackNotificationPreferenceUpdated } from '../../utils/google-analytics';
import { useTranslation } from '../../utils/translations';

type NotificationChannel = 'email' | 'sms';

const NOTIFICATION_CONFIG: Record<
    NotificationChannel,
    {
        field: 'emailNotification' | 'smsNotification';
        icon: string;
        title: string;
        description: string;
        successMessage: string;
        failureMessage: string;
    }
> = {
    email: {
        field: 'emailNotification',
        icon: 'icon-email-notification',
        title: 'Email Notifications',
        description:
            'Receive notifications via email about important updates, payment reminders, and account activity.',
        successMessage: 'Email Notification Preferences Updated Successfully',
        failureMessage: 'Failed To Update Email Notification Preferences',
    },
    sms: {
        field: 'smsNotification',
        icon: 'icon-smartphone',
        title: 'SMS Notifications',
        description: 'Receive text messages for critical alerts, payment confirmations, and time-sensitive updates.',
        successMessage: 'SMS Notification Preferences Updated Successfully',
        failureMessage: 'Failed To Update SMS Notification Preferences',
    },
};

interface NotificationRowProps {
    channel: NotificationChannel;
    checked: boolean;
    disabled: boolean;
    isImpersonating: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ channel, checked, disabled, isImpersonating, onChange }) => {
    const { icon, title, description } = NOTIFICATION_CONFIG[channel];

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={2}
            justifyContent="space-between"
            sx={{ bgcolor: colors.primaryLight[5], borderRadius: 2, p: 2 }}
        >
            <Stack direction="row" alignItems="center" gap={2}>
                <CommonIconAvatar icon={icon} iconPadding={1.6} />
                <Stack gap={0.5}>
                    <Typography variant="subtitle1" component={'h2'} color={colors.fullBlack}>
                        {title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {description}
                    </Typography>
                </Stack>
            </Stack>
            <Box>
                <CustomSwitch
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                    sx={{
                        pointerEvents: isImpersonating ? 'none' : 'auto',
                        cursor: isImpersonating ? 'not-allowed' : 'pointer',
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();

                            onChange({
                                target: {
                                    checked: !checked,
                                },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                />
            </Box>
        </Stack>
    );
};

export const NotificationsTab: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isEmailNotificationsEnabled = !!useSelector((state: RootState) => state.user.userDetails?.EmailNotification);
    const isSMSNotificationsEnabled = !!useSelector((state: RootState) => state.user.userDetails?.SmsNotification);
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const [isUpdatingSMS, setIsUpdatingSMS] = useState(false);
    const impersonation = useSelector((state: RootState) => state.impersonation);

    const handleNotificationChange = async (channel: NotificationChannel, newValue: boolean) => {
        const { field, successMessage, failureMessage } = NOTIFICATION_CONFIG[channel];
        const setIsUpdating = channel === 'email' ? setIsUpdatingEmail : setIsUpdatingSMS;

        setIsUpdating(true);
        try {
            const response = await updateNotificationPreferencesService({ [field]: newValue }, false);
            if (response.statusCode === 200) {
                trackNotificationPreferenceUpdated(channel, newValue);
                dispatch(updateNotificationPreferences({ [field]: newValue }));
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || successMessage,
                        severity: 'success',
                    }),
                );
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || failureMessage,
                        severity: 'error',
                    }),
                );
            }
        } catch (error: any) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: error?.message || failureMessage,
                    severity: 'error',
                }),
            );
        } finally {
            setIsUpdating(false);
        }
    };

    const handleEmailNotificationChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleNotificationChange('email', event.target.checked);

    const handleSMSNotificationChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleNotificationChange('sms', event.target.checked);

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} color={colors.fullBlack} mb={1}>
                {t('settings.notifications.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {t('settings.notifications.subTitle')}
            </Typography>

            <Stack gap={3} mt={3}>
                <NotificationRow
                    channel="email"
                    checked={isEmailNotificationsEnabled}
                    disabled={isUpdatingEmail}
                    isImpersonating={!!impersonation?.isImpersonating}
                    onChange={handleEmailNotificationChange}
                />
                <NotificationRow
                    channel="sms"
                    checked={isSMSNotificationsEnabled}
                    disabled={isUpdatingSMS}
                    isImpersonating={!!impersonation?.isImpersonating}
                    onChange={handleSMSNotificationChange}
                />
            </Stack>
        </Box>
    );
};
